import { FloatingFocusManager } from "@floating-ui/react"
import PropTypes from "prop-types"
import React from "react"

export const FocusManager = ({
  children,
  popoverContext,
  focusOrder = ["content"],
  focusTrap = true,
  returnFocus = true,
  initialFocus = 0,
  ...props
}) => {
  // filtering out style and className
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  return (
    <FloatingFocusManager
      {...rest}
      context={popoverContext}
      order={focusOrder}
      modal={focusTrap}
      returnFocus={returnFocus}
      initialFocus={initialFocus}
    >
      {children}
    </FloatingFocusManager>
  )
}

FocusManager.displayName = "FocusManager"

FocusManager.propTypes = {
  /**
   * React node to render inside Base Popover.
   */
  children: PropTypes.node.isRequired,
  /**
   * context object used to focus on the floating element, pass the `popoverContext` returned by the `usePopover` hook.
   */
  popoverContext: PropTypes.object.isRequired,
  /**
   * The order in which focus cycles.
   *
   * * `reference` - Target element which acts as pivot for Popover.
   * * `floating` - Popover wrapper element.
   * * `content` - Content inside Popover wrapper.
   *
   * By default it's value is ['content'] - Subsequent tabs will cycle through the tabbable contents of the Popover content.
   *
   * If the value is set to ['floating', 'content'] - Includes floating element in focus cycle.
   *
   * If the value is set to ['reference', 'content'] - Includes reference element in focus cycle.
   *
   * * If the value is set to ['reference', 'floating', 'content'] - tabs will cycle through the reference, floating & tabbable contents of the Popover content.
   *
   */
  focusOrder: PropTypes.arrayOf(
    PropTypes.oneOf(["reference", "floating", "content"])
  ),
  /**
   * Add focus trap in the content, outside content cannot be accessed if enabled.
   */
  focusTrap: PropTypes.bool,
  /**
   * Element to initially focus when Popover is opened. Can be either a number or a ref.
   * Number is decided by the tabbable index as specified by the focusOrder.
   */
  initialFocus: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.number,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /**
   * determines if focus should be returned to the reference element.
   */
  returnFocus: PropTypes.bool,
}

FocusManager.defaultProps = {
  focusOrder: ["content"],
  focusTrap: true,
  initialFocus: 0,
  returnFocus: true,
}
